/**
 * @description Above-nav banner when Elavon is on and companies.merchant_alias is empty.
 *
 * Key features:
 * - Visible to every merchant user (payments fail closed without an alias)
 * - Add Alias CTA only when the user has company.edit
 * - Add Alias opens /merchant/company and the existing Company Settings modal at Merchant Alias
 * - Surface uses tenant primary; copy + CTA ink are contrast-measured (never hardcoded white)
 * - Dismiss X hides the banner for this login + that company; company switcher keeps each dismiss
 *
 * @dependencies
 * - CompanyContext company (elavonAssigned + merchantAlias from /auth/me)
 * - PermissionContext for company.edit
 * - BrandingContext primaryColor
 * - resolveBrandBannerColors (WCAG ink vs the actual primary)
 * - missingAliasBannerDismiss (session+company-scoped localStorage)
 *
 * @notes
 * - Do not show when Elavon is off or the alias is already saved
 * - Grey primary must resolve to dark ink, not white — see colorUtils tests
 * - Dismiss is keyed to lynx_auth_session_id + company id, not access_token
 */

import { Link } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import { useCompany } from '../../../contexts/CompanyContext';
import { usePermissions } from '../../../contexts/PermissionContext';
import { useBranding } from '../../../contexts/BrandingContext';
import { PERMISSIONS } from '../../../constants/permissionMappings';
import { resolveBrandBannerColors } from '../../../utils/colorUtils';
import {
  dismissBannerForCurrentSession,
  isBannerDismissedForCurrentSession
} from '../../../utils/missingAliasBannerDismiss';
import { COMPANY_SETTINGS_ALIAS_HREF } from '../../../utils/merchantAliasDeepLink';

/**
 * @param {object} props
 * @param {function} [props.onVisibilityChange]
 */
function MissingMerchantAliasBanner({ onVisibilityChange }) {
  const { company } = useCompany();
  const { hasPermission, isLoaded } = usePermissions();
  const { branding } = useBranding();
  const colors = resolveBrandBannerColors(branding?.colors?.primaryColor);
  const companyId = company?.id;
  const [dismissed, setDismissed] = useState(
    () => isBannerDismissedForCurrentSession(companyId)
  );

  useEffect(() => {
    setDismissed(isBannerDismissedForCurrentSession(companyId));
  }, [companyId]);

  const eligible = Boolean(company?.elavonAssigned && !company?.merchantAlias);
  const visible = eligible && !dismissed;

  useEffect(() => {
    onVisibilityChange?.(visible);
    return () => onVisibilityChange?.(false);
  }, [visible, onVisibilityChange]);

  if (!visible) {
    return null;
  }

  const canEdit = isLoaded && hasPermission(PERMISSIONS.company.edit);

  const handleDismiss = () => {
    dismissBannerForCurrentSession(companyId);
    setDismissed(true);
  };

  return (
    <div
      className="relative h-10 px-4 flex items-center justify-center gap-3 text-sm"
      style={{ backgroundColor: colors.surface, color: colors.ink }}
      role="status"
    >
      <span className="truncate pr-8">
        Input your merchant alias in Company Settings to begin accepting payments
      </span>
      {canEdit && (
        <Link
          to={COMPANY_SETTINGS_ALIAS_HREF}
          className="shrink-0 inline-flex items-center px-2.5 py-1 rounded-md text-xs font-semibold hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-current"
          style={{ backgroundColor: colors.buttonBg, color: colors.buttonFg }}
        >
          Add Alias
        </Link>
      )}
      <button
        type="button"
        onClick={handleDismiss}
        aria-label="Dismiss merchant alias reminder"
        className="absolute right-2 top-1/2 -translate-y-1/2 inline-flex items-center justify-center h-7 w-7 rounded-md hover:opacity-80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-current"
        style={{ color: colors.ink }}
      >
        <X className="h-4 w-4" aria-hidden="true" />
      </button>
    </div>
  );
}

export default MissingMerchantAliasBanner;
